export const useDetailArticlePage = async () => {
  const nuxtApp = useNuxtApp()
  const categoryEventName = 'Article'
  const eventName = 'click_blog_tag'
  const eventShareName = 'click_blog_social_shared'

  const {
    item: article,
    relatedItems: relateArticles,
    breadcrumb: breadcrumbItem,
  } = await usePageDetailApi<Article>({
    path: '/articles/',
    previewPath: '/article-previews/',
    query: { populate: 'articleCategory,articleTags,author' },
  })

  // Update view count (once per session)
  if (import.meta.client && article.value?._id) {
    const viewKey = `viewed-article-${article.value._id}`
    if (!sessionStorage.getItem(viewKey)) {
      const { $api } = nuxtApp
      $api(`/articles/${article.value._id}/view`, { method: 'PATCH' })
        .then(() => sessionStorage.setItem(viewKey, '1'))
        .catch(() => {})
    }
  }

  const tags = computed(() => {
    return article.value?.articleTags?.map(tag => ({
      title: tag.title,
      slug: tag.slug,
    })) || []
  })

  const author = computed(() => article.value?.author || null)
  const authorName = computed(() => author.value?.penName || author.value?.fullName || '')

  useCustomSeo({
    title: () => article.value?.metaTitleFront,
    description: () => article.value?.metaDescriptionFront,
    image: () => article.value?.ogImage?.large || article.value?.imageCover?.large,
    keywords: () => article.value?.focusKeyword,
  })

  const { getPath, slugPageInfo, domain } = useNavigationMenu()
  const { analyticEvent, fbPixelEvent } = useAnalytic()

  const eventArticleRelated = (title: string) => {
    analyticEvent({
      ga4ActionName: 'click_blog_related',
      ga4Label: title,
    })
    fbPixelEvent({
      fbCategory: 'trackCustom',
      fbEvent: 'Blog - Related',
      fbValue: { label: title },
    })
  }

  const { currentLocaleCode } = useLanguage()
  nuxtApp.runWithContext(() => useSchemaOrg([
    defineArticle({
      '@id': `${domain.value}/${article.value?.slug}#article`,
      headline: article.value?.title,
      name: article.value?.metaTitleFront,
      description: article.value?.metaDescriptionFront,
      image: article.value?.ogImage?.large || article.value?.imageCover?.large || '',
      url: `${domain.value}${getPath({ componentName: 'Article', slug: article.value?.slug })}`,
      datePublished: article.value?.publishedAt || '',
      dateModified: article.value?.updatedAt || article.value?.publishedAt || '',
      author: authorName.value
        ? {
            '@type': 'Person',
            name: authorName.value,
          }
        : {
            '@type': 'Organization',
            name: slugPageInfo.value?.title || 'Company',
            '@id': `${domain.value}/#organization`,
          },
      publisher: {
        '@type': 'Organization',
        name: slugPageInfo.value?.title || 'Company',
        '@id': `${domain.value}/#organization`,
        logo: {
          '@id': `${domain.value}/#logo`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${domain.value}${getPath({ componentName: 'Article', slug: article.value?.slug })}#webpage`,
      },
      inLanguage: currentLocaleCode.value,
      isPartOf: { '@id': `${domain.value}/#website` },
    }),
  ]))

  return {
    categoryEventName,
    eventName,
    eventShareName,
    article,
    relateArticles,
    breadcrumbItem,
    tags,
    author,
    getPath,
    slugPageInfo,
    eventArticleRelated,
  }
}
